import { Button, Card, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { Controller } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { routes } from 'src/components/upload/SingleFilePreview';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import { useMyDataStore } from 'src/store/me/me.store';

type Props = {
  hook: any;
};

export default function BandForm({ hook }: Props) {
  const theme = useTheme();

  const permissions = useMyDataStore(
    (state) => state.me?.cnAdminInfo?.permissions?.personalization
  );

  return (
    <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
      <Card>
        <Stack spacing={2} p={4}>
          <Stack>
            <Typography>Ícone:</Typography>
            <RHFUploadSingleFileAccelerator
              name="icon"
              acceptAll={false}
              accept={{ 'image/svg+xml': [] }}
              // @ts-ignore
              onDrop={hook.handleDrop}
            />
          </Stack>
          <RHFTextField name="text" label="Texto" />
          <Stack>
            <Typography>Com seta no final do texto:</Typography>
            <RHFRadioGroupLegacy
              name="hasArrow"
              options={hook.ARROW_OPTIONS}
              getOptionLabel={hook.ARROW_LABEL}
            />
          </Stack>
          <RHFSelect name="urlToRedirect" label="Redirecionamento" variant="outlined">
            {routes.map((option: any) => (
              <MenuItem key={option.url} value={option.url}>
                {option.name}
              </MenuItem>
            ))}
          </RHFSelect>
          <Stack
            direction="row"
            width="100%"
            sx={{
              alignItems: 'center',
            }}
            spacing={4}
          >
            <Controller
              name="startDate"
              control={hook.control}
              render={({ field: fieldC, fieldState: { error } }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Início"
                  slotProps={{
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                    textField: {
                      error: !!error,
                      helperText: error ? error?.message : '',
                    },
                  }}
                  {...fieldC}
                />
              )}
            />
            <Controller
              name="endDate"
              control={hook.control}
              render={({ field: fieldC, fieldState: { error } }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Fim"
                  slotProps={{
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                    textField: {
                      error: !!error,
                      helperText: error ? error?.message : '',
                    },
                  }}
                  {...fieldC}
                />
              )}
            />
          </Stack>
          <RHFTextField name="priority" label="Nível de prioridade" type="number" />
          <Stack>
            <Typography>Status:</Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={hook.STATUS_OPTIONS}
              getOptionLabel={hook.STATUS_LABEL}
            />
          </Stack>
          <Typography>Regras de exibição:</Typography>
          {hook.fields.map((field: any, index: any) => (
            <Stack
              key={field.id}
              spacing={2}
              sx={{ border: '1px solid #ddd', p: 2, borderRadius: 2 }}
            >
              <Typography>Tipo da regra: </Typography>
              <RHFRadioGroupLegacy
                name={`rules.${index}.ruleType`}
                options={hook.INSTANT_TYPE_OPTIONS}
                getOptionLabel={hook.INSTANT_TYPE_LABEL}
              />

              <Stack>
                <Stack
                  direction="row"
                  width="100%"
                  sx={{
                    alignItems: 'center',
                  }}
                  spacing={4}
                >
                  <Controller
                    name={`rules.${index}.startDate`}
                    control={hook.control}
                    render={({ field: fieldC, fieldState: { error } }) => (
                      <DatePicker
                        sx={{
                          flex: 1,
                        }}
                        closeOnSelect
                        label="Data de Início"
                        slotProps={{
                          day: {
                            sx: {
                              '&.MuiPickersDay-root.Mui-selected': {
                                backgroundColor: 'primary.main',
                              },
                            },
                          },
                          textField: {
                            error: !!error,
                            helperText: error ? error?.message : '',
                          },
                        }}
                        {...fieldC}
                      />
                    )}
                  />
                  <Controller
                    name={`rules.${index}.endDate`}
                    control={hook.control}
                    render={({ field: fieldC, fieldState: { error } }) => (
                      <DatePicker
                        sx={{
                          flex: 1,
                        }}
                        closeOnSelect
                        label="Data de Fim"
                        slotProps={{
                          day: {
                            sx: {
                              '&.MuiPickersDay-root.Mui-selected': {
                                backgroundColor: 'primary.main',
                              },
                            },
                          },
                          textField: {
                            error: !!error,
                            helperText: error ? error?.message : '',
                          },
                        }}
                        {...fieldC}
                      />
                    )}
                  />
                </Stack>
              </Stack>

              <Button color="error" onClick={() => hook.remove(index)}>
                Remover Regra
              </Button>
            </Stack>
          ))}
          <Stack direction="row" justifyContent="flex-start">
            <Button
              variant="soft"
              onClick={() =>
                hook.append({
                  ruleType: 'nfScan',
                  startDate: new Date(),
                  endDate: new Date(),
                })
              }
            >
              Adicionar Regra
            </Button>
          </Stack>
          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={hook.isSubmitting}
              disabled={
                (hook.isEdit && !permissions?.update) || (!hook.isEdit && !permissions?.create)
              }
            >
              {!hook.isEdit ? 'Cadastrar Faixa' : 'Editar Faixa'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
